import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { parseAmount } from "../../lib/amount";
import { reportSaveBlocked, reportSaveFailure } from "../../lib/saveNotice";
import { Link, useNavigate, useParams } from "react-router";
import { Button, Card, Cluster, EmptyState, Input, Select, Stack, Tooltip, useToast } from "@salesapp/ui";
import { crmApi, type Opportunity, type Quote, type TimelineEvent } from "../../lib/crmApi";
import { RecordTimeline } from "./RecordTimeline";
import {
  FIELD_HINTS,
  FORECAST_CATEGORY_OPTIONS,
  OPPORTUNITY_STAGE_OPTIONS,
  QUOTE_STATUS_OPTIONS,
} from "../../help/fieldHints";
import { DetailSection, FieldItem, RecordWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { StatusBadge } from "./StatusBadge";
import { FieldLabel } from "./FieldLabel";
import { ProductPicker, type AppliedAgreementLine, type PickedProductLine } from "./ProductPicker";
import { RevertToLeadModal } from "./RevertToLeadModal";
import { RecordOwner } from "./RecordOwner";
import { useCrmHref } from "../../lib/customerUrl";
import { useAccess } from "../../shell/AccessProvider";
import { useLocale } from "../../shell/LocaleProvider";
import { ActivityCommentForm } from "./ActivityCommentForm";
import { ContactLookup } from "./ContactLookup";
import { useLoxleyOffer } from "../../loxley/LoxleyProvider";

const STAGES: Array<{ value: string; label: string; hint: string }> = [
  { value: "lead_in", label: "Lead In", hint: OPPORTUNITY_STAGE_OPTIONS[0].meaning },
  { value: "qualifying", label: "Qualifying", hint: OPPORTUNITY_STAGE_OPTIONS[1].meaning },
  { value: "proposal", label: "Proposal", hint: OPPORTUNITY_STAGE_OPTIONS[2].meaning },
  { value: "negotiation", label: "Negotiation", hint: OPPORTUNITY_STAGE_OPTIONS[3].meaning },
  { value: "closed_won", label: "Closed Won", hint: OPPORTUNITY_STAGE_OPTIONS[4].meaning },
  { value: "closed_lost", label: "Closed Lost", hint: OPPORTUNITY_STAGE_OPTIONS[5].meaning },
];

type Tab = "details" | "quotes";

const PROBABILITY_HINT =
  "Estimated chance of winning this deal (0–100%). It is entered when the opportunity is created or imported, or edited here in the Deal section. It does not change when the stage moves. An opportunity converted from a lead starts with no probability. The forecast multiplies deal value by this percentage.";

function stageLabel(value: string | null | undefined): string {
  return STAGES.find((stage) => stage.value === value)?.label ?? value ?? "Lead In";
}

function SalesProcess({
  current,
  disabled,
  onSelect,
  actions,
}: {
  current: string;
  disabled?: boolean;
  onSelect: (stage: string) => void;
  actions?: ReactNode;
}) {
  const currentIndex = STAGES.findIndex((stage) => stage.value === current);
  return (
    <div className="crm-panel-card">
      <Cluster gap={2} justify="between" align="center">
        <h2 className="crm-section-title">Sales process</h2>
        {actions}
      </Cluster>
      <ol className="crm-sales-process" aria-label="Sales process">
        {STAGES.map((stage, index) => {
          const isCurrent = stage.value === current;
          const isDone = currentIndex >= 0 && index < currentIndex && current !== "closed_lost";
          return (
            <li key={stage.value}>
              <button
                type="button"
                className={`crm-sales-process-step${isCurrent ? " is-current" : ""}${isDone ? " is-done" : ""}`}
                aria-current={isCurrent ? "step" : undefined}
                title={stage.hint}
                disabled={disabled || isCurrent}
                onClick={() => onSelect(stage.value)}
              >
                {stage.label}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function OpportunityDetailPage({ orgId }: { orgId: string }) {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const { can } = useAccess();
  const { show } = useToast();
  const { formatMoney, formatDate } = useLocale();
  const crmHref = useCrmHref();
  const navigate = useNavigate();
  const canEdit = can("crm.opportunities", "update");
  const canAge = can("crm.opportunities.aging");
  const canQuote = can("crm.quotes", "create");
  const canReadQuotes = can("crm.quotes", "read") || canQuote;
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  useLoxleyOffer(opportunity ? { kind: "opportunity", id: opportunity.id, label: opportunity.title } : null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("details");
  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [stage, setStage] = useState("lead_in");
  const [forecastCategory, setForecastCategory] = useState("pipeline");
  const [probability, setProbability] = useState("");
  const [productLines, setProductLines] = useState<PickedProductLine[]>([]);
  const [contactName, setContactName] = useState("");
  const [contactId, setContactId] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  const load = useCallback(async () => {
    if (!opportunityId) return;
    setLoading(true);
    setError(null);
    try {
      const [data, quoteRows, timelineRows] = await Promise.all([
        crmApi.getOpportunity(opportunityId, orgId),
        canReadQuotes
          ? crmApi.listQuotes(orgId, undefined, undefined, opportunityId).catch(() => [] as Quote[])
          : Promise.resolve([] as Quote[]),
        crmApi.getOpportunityTimeline(opportunityId, orgId).catch(() => []),
      ]);
      setOpportunity(data);
      setQuotes(quoteRows);
      setTimeline(timelineRows);
      setTitle(data.title);
      setValue(data.value != null ? String(data.value) : "");
      setCloseDate(data.expected_close_date?.slice(0, 10) ?? "");
      setStage(data.stage ?? "lead_in");
      setForecastCategory(data.forecast_category ?? "pipeline");
      setProbability(data.probability_pct != null ? String(data.probability_pct) : "");
      setContactName(data.primary_contact_name ?? "");
      setContactId(data.primary_contact_id ?? "");
      setProductLines(
        (data.products ?? [])
          .filter((item) => Boolean(item.product_id))
          .map((item) => ({ product_id: item.product_id as string, quantity: item.quantity ?? 1 })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load opportunity");
    } finally {
      setLoading(false);
    }
  }, [opportunityId, orgId, canReadQuotes]);

  const appliedAgreementLines = useMemo(() => {
    const map: Record<string, AppliedAgreementLine> = {};
    for (const item of opportunity?.products ?? []) {
      if (item.product_id && item.agreement_line_code) {
        map[item.product_id] = {
          line_code: item.agreement_line_code,
          agreement_id: item.agreement_id,
          agreement_number: item.agreement_number,
        };
      }
    }
    return map;
  }, [opportunity?.products]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!opportunityId) return;
    // Validate before anything is written, so a bad amount cannot leave a half-saved deal.
    const amount = parseAmount(value, "Value", { allowZero: false });
    if (amount.error || amount.value == null) {
      setError(reportSaveBlocked(show, amount.error ?? "Value must be greater than zero."));
      return;
    }
    const probabilityText = probability.trim();
    const probabilityPct = probabilityText === "" ? null : Number(probabilityText);
    if (probabilityPct !== null && (!Number.isInteger(probabilityPct) || probabilityPct < 0 || probabilityPct > 100)) {
      setError(reportSaveBlocked(show, "Win probability must be a whole number from 0 to 100."));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (stage !== (opportunity?.stage ?? "lead_in")) {
        await crmApi.changeOpportunityStage(opportunityId, { stage }, orgId);
      }
      await crmApi.updateOpportunity(
        opportunityId,
        {
          title,
          value: amount.value,
          expected_close_date: closeDate || null,
          forecast_category: forecastCategory,
          probability_pct: probabilityPct,
          primary_contact_id: contactId || null,
          product_ids: productLines.map((line) => line.product_id),
          product_lines: productLines,
        },
        orgId,
      );
      setEditing(false);
      show("Saved!", "success");
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "Failed to save opportunity"));
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    if (!opportunity) return;
    setTitle(opportunity.title);
    setValue(opportunity.value != null ? String(opportunity.value) : "");
    setCloseDate(opportunity.expected_close_date?.slice(0, 10) ?? "");
    setForecastCategory(opportunity.forecast_category ?? "pipeline");
    setProbability(opportunity.probability_pct != null ? String(opportunity.probability_pct) : "");
    setContactName(opportunity.primary_contact_name ?? "");
    setContactId(opportunity.primary_contact_id ?? "");
    setProductLines(
      (opportunity.products ?? [])
        .filter((item) => Boolean(item.product_id))
        .map((item) => ({ product_id: item.product_id as string, quantity: item.quantity ?? 1 })),
    );
    setEditing(false);
  }

  async function moveToStage(nextStage: string) {
    if (!opportunityId || nextStage === (opportunity?.stage ?? "lead_in")) return;
    setStage(nextStage);
    setError(null);
    try {
      await crmApi.changeOpportunityStage(opportunityId, { stage: nextStage }, orgId);
      show("Saved!", "success");
      await load();
    } catch (err) {
      setStage(opportunity?.stage ?? "lead_in");
      setError(reportSaveFailure(show, err, "Failed to move opportunity"));
    }
  }

  async function generateQuote() {
    if (!opportunityId) return;
    setQuoting(true);
    setError(null);
    try {
      const hadExistingQuote = quotes.length > 0;
      const created = await crmApi.generateQuoteFromOpportunity(opportunityId, orgId);
      show(
        hadExistingQuote
          ? `Quote ${created.quote_number ?? ""} — versioned from the opportunity`.trim()
          : `Quote ${created.quote_number ?? ""} created`.trim(),
        "success",
      );
      navigate(crmHref(`/quotes/${created.id}`));
    } catch (err) {
      setError(reportSaveFailure(show, err, "Failed to generate quote"));
    } finally {
      setQuoting(false);
    }
  }

  async function revertToLead() {
    if (!opportunityId) return;
    setReverting(true);
    setError(null);
    try {
      const result = await crmApi.revertOpportunityToLead(opportunityId, orgId);
      setRevertOpen(false);
      show("Saved!", "success");
      navigate(crmHref(`/leads/${result.lead_id}`));
    } catch (err) {
      setError(reportSaveFailure(show, err, "Failed to move this deal back to a lead"));
    } finally {
      setReverting(false);
    }
  }

  if (loading) return <Card>Loading...</Card>;
  if (error && !opportunity) return <Card className="crm-error">{error}</Card>;
  if (!opportunity) return <Card>Opportunity not found</Card>;

  const latestQuote = quotes[0];
  const inNegotiation = (opportunity.stage ?? "") === "negotiation";

  return (
    <RecordWorkspace
      kind="opportunity"
      objectLabel="Opportunity"
      title={opportunity.title}
      subtitle={opportunity.company_name || undefined}
      backTo={crmHref("/opportunities")}
      backLabel="← Back to Opportunities"
      actions={
        <ResponsiveActions
          primary={
            <>
              {canEdit ? (
                <Select
                  aria-label="Move opportunity to stage"
                  value={opportunity.stage ?? "lead_in"}
                  onChange={(event) => void moveToStage(event.target.value)}
                >
                  {STAGES.map((option) => (
                    <option key={option.value} value={option.value} title={option.hint}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              ) : null}
              {canQuote ? (
                <Button variant={inNegotiation ? "primary" : "secondary"} onClick={() => generateQuote()} disabled={quoting}>
                  {quoting ? "Generating…" : "Generate quote"}
                </Button>
              ) : null}
            </>
          }
          more={
            <>
              {canEdit && opportunity.can_revert_to_lead ? (
                <Button variant="secondary" onClick={() => setRevertOpen(true)}>
                  Move back to lead
                </Button>
              ) : null}
              <DeleteRecordButton
                capability="crm.opportunities"
                recordLabel={opportunity.title}
                onDeleted={async () => {
                  if (!opportunityId) return;
                  try {
                    await crmApi.deleteOpportunity(opportunityId, orgId);
                    navigate(crmHref("/opportunities"));
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Failed to delete opportunity");
                  }
                }}
              />
            </>
          }
        />
      }
      highlights={[
        { label: "Opportunity ID", value: opportunity.opportunity_code },
        { label: "Stage", value: <StatusBadge status={opportunity.stage} options={OPPORTUNITY_STAGE_OPTIONS}>{stageLabel(opportunity.stage)}</StatusBadge> },
        {
          label: "Win probability",
          value: (
            <Tooltip text={PROBABILITY_HINT}>
              <span className="crm-badge">
                {opportunity.probability_pct != null ? `${opportunity.probability_pct}%` : "Not set"}
              </span>
            </Tooltip>
          ),
        },
        { label: "Deal value", value: formatMoney(opportunity.value, opportunity.currency, true) },
        { label: "Company", value: opportunity.company_name },
        {
          label: "Salesperson",
          value: (
            <RecordOwner
              orgId={orgId}
              kind="opportunities"
              recordId={opportunity.id}
              ownerUserId={opportunity.owner_user_id}
              ownerName={opportunity.owner_name}
              canReassign={opportunity.can_reassign_owner}
              onChanged={(owner) => setOpportunity((prev) => (prev ? { ...prev, ...owner } : prev))}
            />
          ),
        },
        { label: "Lead ID", value: opportunity.source_lead_code },
        ...(latestQuote
          ? [{ label: "Latest quote", value: latestQuote.quote_number ?? "Quote" }]
          : []),
        ...(canAge
          ? [{
              label: "Aging",
              value: (
                <span className="crm-badge crm-badge--aging">
                  {opportunity.age_days == null || opportunity.age_days <= 0
                    ? "Today"
                    : opportunity.age_days === 1
                      ? "1 day"
                      : `${opportunity.age_days} days`}
                </span>
              ),
            }]
          : []),
      ]}
      tabs={[
        { key: "details", label: "Details" },
        ...(canReadQuotes ? [{ key: "quotes", label: `Quotes (${quotes.length})` }] : []),
      ]}
      activeTab={tab}
      onTabChange={(key) => setTab(key as Tab)}
      aside={
        <>
          <div className="crm-aside-panel">
            <Cluster gap={2} justify="between" align="center">
              <h2 className="crm-aside-title">Stage history</h2>
              {opportunity.company_id ? (
                <Button variant="ghost" size="sm" onClick={() => navigate(crmHref(`/companies/${opportunity.company_id}?tab=activities`))}>
                  View activities
                </Button>
              ) : null}
            </Cluster>
            <div className="crm-quick-links">
              {opportunity.company_id ? (
                <Link to={crmHref(`/companies/${opportunity.company_id}`)} className="crm-quick-link">
                  <strong>1</strong>
                  Company
                </Link>
              ) : (
                <div className="crm-quick-link">
                  <strong>0</strong>
                  Company
                </div>
              )}
              {opportunity.source_lead_id ? (
                <Link to={crmHref(`/leads/${opportunity.source_lead_id}`)} className="crm-quick-link">
                  <strong>{opportunity.source_lead_code ?? "Lead"}</strong>
                  Source lead
                </Link>
              ) : (
                <div className="crm-quick-link">
                  <strong>0</strong>
                  Source lead
                </div>
              )}
              {canReadQuotes ? (
                <button type="button" className="crm-quick-link" onClick={() => setTab("quotes")}>
                  <strong>{quotes.length}</strong>
                  Quotes
                </button>
              ) : null}
            </div>
          </div>
          <div className="crm-aside-panel">
            <h2 className="crm-aside-title">Opportunity timeline</h2>
            <RecordTimeline
              events={timeline}
              emptyTitle="No timeline events yet"
              emptyDescription="Stage moves, quotes, and orders for this opportunity will appear here."
            />
          </div>
        </>
      }
    >
      {error && <p className="crm-error">{error}</p>}
      {tab === "details" && (
        <Stack gap={4}>
          <ActivityCommentForm
            orgId={orgId}
            companyId={opportunity.company_id}
            opportunityId={opportunity.id}
            defaultSubject={opportunity.opportunity_code ? `Comment on Opportunity ID: ${opportunity.opportunity_code}` : "Comment on opportunity"}
            onSaved={load}
          />
          <SalesProcess
            current={opportunity.stage ?? "lead_in"}
            disabled={!canEdit}
            onSelect={(next) => void moveToStage(next)}
            actions={
              canEdit ? (
                editing ? (
                  <Cluster gap={2}>
                    <Button onClick={() => save()} disabled={!title.trim() || saving}>
                      {saving ? "Saving…" : "Save"}
                    </Button>
                    <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
                      Cancel
                    </Button>
                  </Cluster>
                ) : (
                  <Button onClick={() => setEditing(true)}>Edit</Button>
                )
              ) : null
            }
          />
          <DetailSection title="Converted from">
            <FieldItem
              label="Lead"
              hint={FIELD_HINTS.opportunity_source_lead}
              value={
                opportunity.source_lead_id ? (
                  <Link to={crmHref(`/leads/${opportunity.source_lead_id}`)}>
                    {opportunity.source_lead_code || "Open lead"}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <FieldItem
              label="Company"
              hint={FIELD_HINTS.opportunity_company}
              value={
                opportunity.company_id ? (
                  <Link to={crmHref(`/companies/${opportunity.company_id}`)}>{opportunity.company_name ?? "Company"}</Link>
                ) : (
                  opportunity.company_name
                )
              }
            />
            <FieldItem
              label="Name"
              value={
                editing ? (
                  <ContactLookup
                    orgId={orgId}
                    companyId={opportunity.company_id || undefined}
                    value={contactName}
                    contactId={contactId}
                    onChange={({ name, contactId: nextId }) => {
                      setContactName(name);
                      setContactId(nextId);
                    }}
                  />
                ) : opportunity.primary_contact_id && opportunity.company_id ? (
                  <Link to={crmHref(`/companies/${opportunity.company_id}?contact=${opportunity.primary_contact_id}`)}>
                    {opportunity.primary_contact_name ?? "Open contact"}
                  </Link>
                ) : (
                  opportunity.primary_contact_name ?? "— (not known yet)"
                )
              }
            />
            <FieldItem label="Phone" value={opportunity.primary_contact_phone || "—"} />
            <FieldItem label="Email" value={opportunity.primary_contact_email || "—"} />
            <FieldItem label="Opportunity ID" hint={FIELD_HINTS.opportunity_id} value={opportunity.opportunity_code} />
            {latestQuote ? (
              <FieldItem
                label="Latest quote"
                hint={FIELD_HINTS.opportunity_latest_quote}
                value={<Link to={crmHref(`/quotes/${latestQuote.id}`)}>{latestQuote.quote_number}</Link>}
              />
            ) : null}
          </DetailSection>

          <div className="crm-panel-card">
            <h2 className="crm-section-title">Deal</h2>
            <Stack gap={3}>
              <Input
                label="Title"
                tooltip={FIELD_HINTS.opportunity_title}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={!canEdit}
              />
              <Input
                label="Amount"
                tooltip={FIELD_HINTS.opportunity_amount}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                disabled={!canEdit}
              />
              <Input
                label="Expected close"
                tooltip={FIELD_HINTS.opportunity_expected_close}
                type="date"
                value={closeDate}
                onChange={(event) => setCloseDate(event.target.value)}
                disabled={!canEdit}
              />
              <Input
                label="Win probability (%)"
                tooltip={PROBABILITY_HINT}
                type="number"
                min={0}
                max={100}
                step={1}
                value={probability}
                onChange={(event) => setProbability(event.target.value)}
                disabled={!canEdit}
              />
              <FieldLabel htmlFor="opp-detail-stage" hint={FIELD_HINTS.opportunity_stage}>
                Stage
              </FieldLabel>
              <select
                id="opp-detail-stage"
                className="crm-select"
                value={stage}
                disabled={!canEdit}
                onChange={(event) => setStage(event.target.value)}
              >
                {STAGES.map((option) => (
                  <option key={option.value} value={option.value} title={option.hint}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FieldLabel htmlFor="opp-forecast-category" hint={FIELD_HINTS.opportunity_forecast_category}>
                Forecast category
              </FieldLabel>
              <select
                id="opp-forecast-category"
                className="crm-select"
                value={forecastCategory}
                disabled={!canEdit}
                onChange={(event) => setForecastCategory(event.target.value)}
              >
                <option value="commit" title={FORECAST_CATEGORY_OPTIONS[0].meaning}>
                  Commit
                </option>
                <option value="upside" title={FORECAST_CATEGORY_OPTIONS[1].meaning}>
                  Upside
                </option>
                <option value="pipeline" title={FORECAST_CATEGORY_OPTIONS[2].meaning}>
                  Pipeline
                </option>
              </select>
              <ProductPicker
                orgId={orgId}
                value={productLines}
                quantities
                onChange={setProductLines}
                applied={appliedAgreementLines}
                onTotalChange={(total) => {
                  if (total != null) setValue(String(total));
                }}
              />
              {canEdit ? (
                <div className="crm-form-actions">
                  <Button onClick={() => save()} disabled={!title.trim() || saving}>
                    {saving ? "Saving…" : "Save opportunity"}
                  </Button>
                </div>
              ) : null}
            </Stack>
          </div>
        </Stack>
      )}

      {tab === "quotes" && (
        <div className="crm-panel-card">
          <Cluster gap={3} justify="between">
            <h2 className="crm-section-title">Quote history</h2>
            {canQuote ? (
              <Button onClick={() => generateQuote()} disabled={quoting}>
                {quoting ? "Generating…" : "Generate quote"}
              </Button>
            ) : null}
          </Cluster>
          <p className="crm-help">
            Negotiation usually means a quote is in play. Generate quote creates the quote for this deal, or — if one
            already exists — updates it as a new version (see the Versions tab on the quote). Quote events also appear
            on the company activity timeline.
          </p>
          {quotes.length === 0 ? (
            <EmptyState
              title="No quotes yet"
              description="Generate a quote to start the negotiation paper trail."
              action={
                canQuote ? (
                  <Button onClick={() => generateQuote()} disabled={quoting}>
                    {quoting ? "Generating…" : "Generate quote"}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <table className="crm-table" aria-label="Quote history">
              <thead>
                <tr>
                  <th>Quote</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <tr key={quote.id}>
                    <td>
                      <Link to={crmHref(`/quotes/${quote.id}`)}>{quote.quote_number}</Link>
                    </td>
                    <td>
                      <StatusBadge
                        status={quote.quote_status || "draft"}
                        options={QUOTE_STATUS_OPTIONS}
                        className={`crm-badge crm-badge--${quote.quote_status || "draft"}`}
                      >
                        {quote.quote_status || "draft"}
                      </StatusBadge>
                    </td>
                    <td>{formatMoney(quote.total, quote.currency, true)}</td>
                    <td>{quote.created_at ? formatDate(quote.created_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <RevertToLeadModal
        open={revertOpen}
        leadLabel={
          opportunity.source_lead_code
            ? `${opportunity.source_lead_code}${opportunity.source_lead_name ? ` · ${opportunity.source_lead_name}` : ""}`
            : opportunity.source_lead_name
        }
        saving={reverting}
        onCancel={() => setRevertOpen(false)}
        onConfirm={() => revertToLead()}
      />
    </RecordWorkspace>
  );
}
